import { useState, useRef, useCallback } from "react";
import { UploadCloud, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { api, asset } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfig";

const CopyField = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true); toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div>
      <p className="font-feature text-[11px] uppercase tracking-[0.25em] text-teal mb-2">{label}</p>
      <div className="flex gap-3">
        <input readOnly value={value} data-testid={`${testid}-input`} onFocus={(e) => e.target.select()}
          className="flex-1 bg-obsidian border border-[#2A201A] px-4 py-3 font-mono text-xs text-bone focus:outline-none focus:border-teal truncate" />
        <button data-testid={`${testid}-copy`} onClick={copy} title={`Copy ${label}`}
          className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-5 hover:bg-bone transition-colors">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

export default function Upload() {
  useTitle("Upload | Aurora Springs RP", "Share your Aurora Springs RP screenshots and clips with the community gallery.");
  const { img } = useSiteConfig();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setBusy(true); setResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload", form, { params: { title: file.name }, headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Uploaded! Copy a link below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Try again.");
    } finally { setBusy(false); }
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); };
  const fullUrl = result ? asset(result.url) : "";
  const htmlCode = fullUrl ? `<img src="${fullUrl}" alt="${result?.original_filename || "image"}" />` : "";
  const forumCode = fullUrl ? `[img]${fullUrl}[/img]` : "";

  return (
    <>
      <PageHeader overline="Share your frontier" title="Image Upload" image={img("upload", IMAGES.aurora)}
        sub="Host a screenshot and instantly grab the links you need to post it in county — Direct URL, HTML and Forum code." />
      <section className="mx-auto max-w-3xl px-6 sm:px-10 py-16">
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed p-16 text-center transition-colors ${drag ? "border-teal bg-teal/5" : "border-[#2A201A] bg-surface/40 hover:border-teal/40"}`}>
          <input ref={inputRef} type="file" accept="image/*" hidden data-testid="upload-input"
            onChange={(e) => doUpload(e.target.files?.[0])} />
          {busy ? (
            <Loader2 className="mx-auto h-10 w-10 text-teal animate-spin" />
          ) : (
            <UploadCloud className="mx-auto h-10 w-10 text-teal" />
          )}
          <p className="mt-6 font-display text-2xl font-light">{busy ? "Uploading…" : "Drag & drop your image"}</p>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-[#8F877C]">or click to browse — JPG, PNG, GIF, WEBP · max 10MB</p>
          <p data-testid="upload-expiration-note" className="mt-2 font-mono text-[11px] uppercase tracking-widest text-gold/80">Public upload links expire after 15 days</p>
        </div>

        {result && (
          <div data-testid="upload-result" className="mt-10 border border-teal/30 bg-surface/60 p-6 aurora-glow space-y-6">
            <div className="overflow-hidden border border-[#2A201A]">
              <img src={fullUrl} alt={result.original_filename} className="w-full max-h-80 object-contain bg-obsidian" />
            </div>
            <CopyField label="Direct URL" value={fullUrl} testid="upload-direct" />
            <CopyField label="HTML" value={htmlCode} testid="upload-html" />
            <CopyField label="Forum Link (BBCode)" value={forumCode} testid="upload-forum" />
            <p className="font-mono text-[11px] text-[#8F877C]">Paste the Direct URL in Discord, the HTML on a webpage, or the Forum code on a BBCode forum.</p>
          </div>
        )}
      </section>
    </>
  );
}
